import { useEffect, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { CheckCircle2, Home } from "lucide-react";
import yLogoDark from "@/assets/brand/y-logo-dark.png";
import { Seo } from "@/lib/useSeo";

/**
 * HelpDeleteSuccess — final confirmation screen after a successful
 * account deletion.  Rendered on a public route.
 *
 * Guard: only shows if the `brontoy_delete_done` sessionStorage marker
 * is present (set by HelpDeleteConfirm right before the hard-reload).
 * Any direct visit without the marker is bounced to `/`.
 */
export default function HelpDeleteSuccess() {
  const nav = useNavigate();

  const allowed = useMemo(() => {
    try {
      return sessionStorage.getItem("brontoy_delete_done") === "1";
    } catch {
      return false;
    }
  }, []);

  // Consume the flag so a refresh doesn't leave the success screen
  // reachable forever.  A single mount is enough for the user to read.
  useEffect(() => {
    // Give the DOM a beat, then clear.
    const t = setTimeout(() => {
      try {
        sessionStorage.removeItem("brontoy_delete_done");
      } catch {
        /* storage disabled */
      }
    }, 3000);
    return () => clearTimeout(t);
  }, []);

  if (!allowed) return <Navigate to="/" replace />;

  return (
    <div
      className="min-h-dvh w-full bg-black text-white flex flex-col relative overflow-hidden"
      data-testid="help-delete-success-screen"
      style={{
        fontFamily:
          "'Plus Jakarta Sans','TwitterChirp','Segoe UI',Helvetica,Arial,sans-serif",
      }}
    >
      <Seo
        title="Account Deleted — BrontoY"
        description="Your BrontoY account has been permanently deleted."
        canonicalPath="/help/delete/done"
      />

      {/* Cool, calm ambient — signals resolution, not destruction */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div
          style={{
            position: "absolute",
            top: "-20%",
            left: "-10%",
            width: "720px",
            height: "720px",
            background:
              "radial-gradient(closest-side, rgba(168,85,247,0.28), transparent)",
            filter: "blur(50px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-25%",
            right: "-15%",
            width: "700px",
            height: "700px",
            background:
              "radial-gradient(closest-side, rgba(59,130,246,0.24), transparent)",
            filter: "blur(50px)",
          }}
        />
      </div>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-[560px] flex flex-col items-center text-center">
          {/* Y logo with celebratory glow */}
          <div className="relative mb-8">
            <img
              src={yLogoDark}
              alt="BrontoY"
              className="h-28 md:h-36 w-auto drop-shadow-[0_0_80px_rgba(168,85,247,0.5)] animate-[float_4s_ease-in-out_infinite]"
              draggable={false}
              data-testid="help-delete-success-logo"
            />
            <span
              className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #22c55e, #16a34a)",
                boxShadow: "0 12px 30px -10px rgba(34,197,94,0.6)",
              }}
            >
              <CheckCircle2 size={22} className="text-white" strokeWidth={2.4} />
            </span>
          </div>

          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/40 mb-3">
            Farewell
          </p>
          <h1
            className="text-[30px] md:text-[42px] font-extrabold tracking-tight leading-[1.1]"
            data-testid="help-delete-success-title"
          >
            Your account has been
            <br />
            deleted successfully.
          </h1>
          <p className="mt-5 text-white/65 text-[15px] md:text-[16px] leading-relaxed max-w-[440px]">
            All of your BrontoY data — profile, posts, followers, saved items —
            has been permanently removed. Your session has ended on this device.
          </p>
          <p className="mt-3 text-white/45 text-[13.5px] leading-relaxed max-w-[420px]">
            Thank you for being part of the BrontoY community. If you ever want
            to come back, you can always sign up again with the same email.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3 w-full max-w-[360px]">
            <button
              type="button"
              onClick={() => nav("/")}
              className="w-full h-[50px] rounded-full bg-white text-black font-bold text-[15px] flex items-center justify-center gap-2 hover:opacity-90 active:opacity-80 transition-opacity"
              data-testid="help-delete-success-home"
            >
              <Home size={16} strokeWidth={2.4} />
              Return home
            </button>
          </div>

          <p className="mt-8 text-white/40 text-[12.5px]">
            BrontoY <span className="text-white/25">·</span> By Bronto Intelligence
          </p>
        </div>
      </main>
    </div>
  );
}
